import { ProductItem } from './products.types';
import api from '../../utils/api';

export class ProductsModel {
  private static STORAGE_KEY = 'mastercoifa_products_db';

  private static defaultProducts: ProductItem[] = [];

  public static getProducts(): ProductItem[] {
    const raw = localStorage.getItem(this.STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.defaultProducts));
      return this.defaultProducts;
    }
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        // Remove estritamente os produtos fictícios antigos de simulações passadas
        return parsed.filter(p => p.id !== '001' && p.id !== '002' && p.name !== 'Coifa Alpha Gourmet' && p.name !== 'Filtro Inercial');
      }
      return this.defaultProducts;
    } catch {
      return this.defaultProducts;
    }
  }

  public static saveProducts(products: ProductItem[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(products));
  }

  public static async fetchProducts(): Promise<ProductItem[]> {
    try {
      const response = await api.get('/products');
      if (response.data && Array.isArray(response.data)) {
        const list = response.data as ProductItem[];
        this.saveProducts(list);
        return list;
      }
    } catch (err) {
      console.warn("Products API Offline or Error, falling back to local storage:", err);
    }
    return this.getProducts();
  }

  public static async saveProductToServer(product: ProductItem, isNew: boolean = false): Promise<boolean> {
    try {
      const categories = localStorage.getItem('mastercoifa_categories_db');
      let categoryId: number | null = null;
      if (categories) {
        try {
          const parsedCats = JSON.parse(categories);
          if (Array.isArray(parsedCats)) {
            const foundCat = parsedCats.find(c => c.name.toLowerCase() === product.category.toLowerCase());
            if (foundCat) {
              categoryId = foundCat.id;
            }
          }
        } catch {}
      }

      const list = this.getProducts();
      const idx = list.findIndex(p => p.id === product.id);
      if (idx >= 0 && !isNew) {
        list[idx] = product;
      } else {
        list.push(product);
      }
      this.saveProducts(list);

      await api.post('/products', {
        id: isNew ? null : (isNaN(parseInt(product.id, 10)) ? null : parseInt(product.id, 10)),
        erpProductId: product.erpProductId || null,
        name: product.name,
        category: product.category,
        categoryId,
        model: product.model,
        desc: product.desc,
        productImage: product.productImage,
        projectImage1: product.projectImage1,
        projectImage2: product.projectImage2,
        accessories: product.accessories || [],
        pieces: product.pieces || [],
        productionSequence: product.productionSequence || []
      });
      return true;
    } catch (err) {
      console.error("Error saving product to server:", err);
      return false;
    }
  }

  public static async deleteProductFromServer(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await api.post('/products/delete', { id: parseInt(id, 10) });
      if (res.data && res.data.success) {
        this.deleteProduct(id);
        return { success: true };
      }
      return { success: false, error: res.data?.error || res.data?.message || 'Não foi possível excluir o produto.' };
    } catch (err: any) {
      console.error("Error deleting product from server:", err);
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erro ao conectar ao servidor para excluir o produto.';
      return { success: false, error: errMsg };
    }
  }

  /**
   * Safe save action: compares old product images with new ones and performs database/server simulated deletion to avoid orphaned files.
   */
  public static saveWithImageCleanup(oldProduct: ProductItem | null, newProduct: ProductItem): void {
    const list = this.getProducts();

    // Check cleanup for ProductImage
    if (oldProduct) {
      if (oldProduct.productImage && oldProduct.productImage !== newProduct.productImage) {
        this.logImageDeletion('productImage', oldProduct.id, oldProduct.name);
      }
      if (oldProduct.projectImage1 && oldProduct.projectImage1 !== newProduct.projectImage1) {
        this.logImageDeletion('projectImage1', oldProduct.id, oldProduct.name);
      }
      if (oldProduct.projectImage2 && oldProduct.projectImage2 !== newProduct.projectImage2) {
        this.logImageDeletion('projectImage2', oldProduct.id, oldProduct.name);
      }
    }

    const idx = list.findIndex(p => p.id === newProduct.id);
    if (idx >= 0) {
      list[idx] = newProduct;
    } else {
      list.push(newProduct);
    }

    this.saveProducts(list);
  }

  /**
   * Safe delete action: cleans up any related images from server / database simulated.
   */
  public static deleteProduct(id: string): boolean {
    const list = this.getProducts();
    const item = list.find(p => p.id === id);
    if (!item) return false;

    // Log deletion of all associated images to satisfy server policy
    if (item.productImage) this.logImageDeletion('productImage', item.id, item.name);
    if (item.projectImage1) this.logImageDeletion('projectImage1', item.id, item.name);
    if (item.projectImage2) this.logImageDeletion('projectImage2', item.id, item.name);

    const filtered = list.filter(p => p.id !== id);
    this.saveProducts(filtered);
    return true;
  }

  private static logImageDeletion(field: string, productId: string, name: string) {
    console.log(`[STORAGE SERVER CLEANUP] Imagem associada ao campo "${field}" do produto "${name}" (ID ${productId}) foi deletada fisicamente do servidor (/uploads/products/) e limpa do banco de dados.`);
  }
}
